/**
 * DictionaryParamBindingsEditor — Etap 10.5 (ADR-0066 §11), przepisany w Fazie 2b
 * (ADR-0068, model dwuetapowy parametrów).
 *
 * Widget wspólny dla WSZYSTKICH edytorów definicji pola select/multiselect
 * (`AddSystemFieldModal`, `AddKartotekaFieldModal`, `ModuleTemplateEditor`
 * → `FieldFormModal`, `AdditionalTablesTab` → `AddFieldPanel`) — konfiguruje
 * WIĄZANIA (etap 2) dla WSZYSTKICH parametrów zadeklarowanych przez słownik
 * zewnętrzny (etap 1: nazwa + typ + required, bez `kind`).
 *
 * Per parametr: selektor RODZAJU wiązania (pole rekordu / bieżąca firma /
 * zalogowany użytkownik / stała) + kontrolka wartości zależna od rodzaju
 * (dropdown pól-sąsiadów dla `record_field`, input tekstowy dla `constant`;
 * `context.*` nie mają wartości — rozwiązuje je serwer). Pusty wybór = brak
 * wiązania (parametr spada na wiązanie DOMYŚLNE źródła, jeśli istnieje).
 *
 * Renderuje SIĘ TYLKO gdy `params` zawiera przynajmniej jeden zadeklarowany
 * parametr — słownik ręczny albo zewnętrzny bez parametrów → `null`, brak UI.
 *
 * Persystencja: mapa `{ [nazwa parametru]: wiązanie }` — DOKŁADNIE kształt
 * `dictionaryParamBindings` na definicji pola. NOWE zapisy są zawsze TAGOWANE
 * (`{ kind: ... }`); legacy goły string (stare wiersze jsonb) jest czytany jako
 * `record_field` ze slugiem = ta wartość. Pusta mapa normalizowana do `null`
 * (konwencja reszty repo — `null` = brak wiązań).
 *
 * Opcjonalny rodzaj `parent_field` („Pole kartoteki: <nazwa>", prop `parentField`) włącza WYŁĄCZNIE
 * ekran tabel dodatkowych: wartość parametru z pola rekordu-RODZICA wiersza. Dla Dokumentów pole
 * wybiera się osobno per klasa (`fieldSlugs` kluczowane klasą), dla pozostałych właścicieli jeden
 * selektor (`'default'`). Wiązanie jest emitowane, gdy wybrano pole przynajmniej dla jednej klasy;
 * wyczyszczenie ostatniej usuwa je z mapy. Bez propa `parentField` edytor działa jak dotąd.
 *
 * Wartość stałej (`constant`) jest emitowana jako STRING — koercję do
 * zadeklarowanego typu parametru robi serwer przy wykonaniu zapytania
 * (`ExternalParamInvalidError` przy niekoercowalnej wartości); typ jest tu
 * pokazywany wyłącznie informacyjnie obok nazwy parametru.
 *
 * Caller odpowiada za:
 *  - dostarczenie `params` (z `useDictionary(code).dictionary.params` w scope
 *    tenanta, albo z rejestru źródeł / detalu słownika globalnego w scope
 *    panelu ITE — identyczny kształt `{name,type,required}`),
 *  - dostarczenie `siblingFields` (pola tej samej encji/modułu/tabeli, z
 *    wykluczeniem edytowanego pola samego siebie, gdy dotyczy),
 *  - wyczyszczenie `value` na `null` gdy user zmieni `dictionaryCode` (inny
 *    słownik = inne parametry, stare wiązania nie mają sensu).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import {
  type DictionaryParamBinding,
  type DictionaryParamBindingsMap,
  normalizeDictionaryParamBinding,
  type ParentFieldBindingKey,
} from '@/hooks/useDictionary';

/** Kształt deklaracji parametru (etap 1) — duck-typed, wspólny dla `DictionaryParam`
 *  (tenant) i `DictionarySourceParam` (panel ITE) — obie strony mają identyczne 3 pola. */
export interface DictionaryBindingParam {
  name: string;
  /** Typ zadeklarowany (text/int/decimal/date/bool) — pokazywany informacyjnie. */
  type: string;
  required: boolean;
}

/** Pole-sąsiad tej samej encji/modułu/tabeli — kandydat na wiązanie `record_field`. */
export interface DictionaryBindingSiblingField {
  slug: string;
  name: string;
}

/** Rodzaje wiązania wybieralne w selektorze (pusty string = brak wiązania). */
type BindingKindOption = '' | DictionaryParamBinding['kind'];

/** Jeden wybór pola rekordu-rodzica: klasa dokumentu albo `'default'` (każdy inny właściciel). */
export interface DictionaryParentFieldKey {
  key: ParentFieldBindingKey;
  /** Etykieta nad selektorem (nazwa klasy dokumentu); `null` = jeden selektor bez etykiety. */
  label: string | null;
  /** Pola kartoteki-rodzica (jedna wartość, bez wrażliwych) — kandydaci na wiązanie. */
  fields: DictionaryBindingSiblingField[];
  /** Katalog pól jeszcze się wczytuje — selektor zablokowany. */
  isLoading?: boolean;
  /** Wczytanie katalogu pól się nie powiodło — pokazujemy komunikat zamiast pustej listy. */
  loadError?: boolean;
}

/**
 * Włącza rodzaj wiązania `parent_field` („Pole kartoteki: <nazwa>"): wartość parametru z pola
 * rekordu-RODZICA tabeli dodatkowej. Podawany WYŁĄCZNIE przez ekran tabel dodatkowych — pozostałe
 * ekrany (pola encji, szablon modułu) nie mają rekordu-rodzica i nie dostają tej opcji.
 */
export interface DictionaryParentFieldConfig {
  /** Etykieta rodzaju w selektorze, np. „Pole kartoteki: Pracownicy". */
  label: string;
  /** Selektory pola: jeden (`'default'`) albo jeden na klasę dokumentu. */
  keys: DictionaryParentFieldKey[];
}

export interface DictionaryParamBindingsEditorProps {
  /** WSZYSTKIE parametry zadeklarowane przez źródło (etap 1) — bez filtrowania po rodzaju. */
  params: DictionaryBindingParam[] | null | undefined;
  /** Pola-sąsiedzi (kandydaci na `record_field`), bez edytowanego pola samego siebie. */
  siblingFields: DictionaryBindingSiblingField[];
  /** Aktualne wiązania: nazwa parametru → wiązanie (tagowane lub legacy string). */
  value: DictionaryParamBindingsMap | null | undefined;
  onChange: (bindings: DictionaryParamBindingsMap | null) => void;
  /** Nadpisanie etykiety rodzaju `record_field` (domyślnie „Pole rekordu"). */
  recordFieldLabel?: string;
  /** Włącza rodzaj `parent_field`; brak = edytor działa jak dotąd (bez nowej opcji). */
  parentField?: DictionaryParentFieldConfig | null;
  'data-testid'?: string;
}

export function DictionaryParamBindingsEditor({
  params,
  siblingFields,
  value,
  onChange,
  recordFieldLabel,
  parentField = null,
  'data-testid': testId = 'dictionary-param-bindings-editor',
}: DictionaryParamBindingsEditorProps) {
  const { t } = useTranslation();

  // Rodzaj wybrany w selektorze, ale jeszcze BEZ kompletnej wartości (record_field bez
  // sluga / constant bez tekstu) — takie wiązanie nie jest emitowane do `value` (niekompletne
  // = brak wiązania), więc sam wybór rodzaju musi żyć w lokalnym stanie do czasu dopełnienia.
  const [draftKinds, setDraftKinds] = useState<Record<string, BindingKindOption>>({});

  const declaredParams = params ?? [];
  if (declaredParams.length === 0) {
    return null;
  }

  const bindings = value ?? {};

  function emit(next: DictionaryParamBindingsMap) {
    onChange(Object.keys(next).length > 0 ? next : null);
  }

  function removeBinding(paramName: string) {
    const next = { ...bindings };
    delete next[paramName];
    emit(next);
  }

  function setBinding(paramName: string, binding: DictionaryParamBinding) {
    emit({ ...bindings, [paramName]: binding });
  }

  function handleKindChange(paramName: string, kind: BindingKindOption) {
    setDraftKinds((prev) => ({ ...prev, [paramName]: kind }));
    if (kind === 'context.company' || kind === 'context.user') {
      // Kompletne od razu — nie potrzebują wartości (rozwiązuje serwer).
      setBinding(paramName, { kind });
      return;
    }
    // '' (brak wiązania) / record_field / constant — do czasu dopełnienia wartości
    // wpis znika z mapy (niekompletne wiązanie nie jest zapisywalne).
    removeBinding(paramName);
  }

  function handleSiblingChange(paramName: string, siblingSlug: string) {
    if (siblingSlug) {
      setBinding(paramName, { kind: 'record_field', fieldSlug: siblingSlug });
    } else {
      removeBinding(paramName);
    }
  }

  function handleParentFieldChange(paramName: string, key: ParentFieldBindingKey, slug: string) {
    const current = normalizeDictionaryParamBinding(bindings[paramName]);
    const nextSlugs: Partial<Record<ParentFieldBindingKey, string>> =
      current?.kind === 'parent_field' ? { ...current.fieldSlugs } : {};
    if (slug) {
      nextSlugs[key] = slug;
    } else {
      delete nextSlugs[key];
    }
    // Rodzaj zostaje „pole kartoteki" także po wyczyszczeniu ostatniej klasy — selektory nie mogą
    // zniknąć użytkownikowi spod ręki (bez wartości wiązanie i tak nie jest emitowane).
    setDraftKinds((prev) => ({ ...prev, [paramName]: 'parent_field' }));
    if (Object.keys(nextSlugs).length > 0) {
      setBinding(paramName, { kind: 'parent_field', fieldSlugs: nextSlugs });
    } else {
      removeBinding(paramName);
    }
  }

  function handleConstantChange(paramName: string, text: string) {
    if (text.length > 0) {
      setBinding(paramName, { kind: 'constant', value: text });
    } else {
      removeBinding(paramName);
    }
  }

  // Pusta opcja MUSI być zwykłą (niedisabled) pozycją listy, nie `placeholder` Dropdowna
  // (placeholder nie jest wybieralny — użytkownik nie mógłby wrócić do „brak wiązania" dla
  // parametru raz zmapowanego, poza resetem całego pickera słownika).
  const kindOptions = [
    { value: '', label: t('common.dictionaryPicker.bindingKind.none') },
    {
      value: 'record_field',
      label: recordFieldLabel ?? t('common.dictionaryPicker.bindingKind.recordField'),
    },
    ...(parentField ? [{ value: 'parent_field', label: parentField.label }] : []),
    { value: 'context.company', label: t('common.dictionaryPicker.bindingKind.contextCompany') },
    { value: 'context.user', label: t('common.dictionaryPicker.bindingKind.contextUser') },
    { value: 'constant', label: t('common.dictionaryPicker.bindingKind.constant') },
  ];

  const siblingOptions = [
    { value: '', label: t('common.dictionaryPicker.paramBindingFieldPlaceholder') },
    ...siblingFields.map((f) => ({ value: f.slug, label: f.name })),
  ];

  return (
    <div
      className="space-y-3 rounded-md border border-border-primary bg-bg-secondary p-3"
      data-testid={testId}
    >
      <div>
        <p className="text-sm font-medium text-text-primary">
          {t('common.dictionaryPicker.paramBindingTitle')}
        </p>
        <p className="mt-0.5 text-xs text-text-muted">
          {t('common.dictionaryPicker.paramBindingHint')}
        </p>
      </div>

      <div className="space-y-3">
        {declaredParams.map((param) => {
          const binding = normalizeDictionaryParamBinding(bindings[param.name]);
          const kind: BindingKindOption = binding ? binding.kind : (draftKinds[param.name] ?? '');
          const siblingSlug = binding?.kind === 'record_field' ? binding.fieldSlug : '';
          const constantText = binding?.kind === 'constant' ? String(binding.value) : '';
          const parentSlugs = binding?.kind === 'parent_field' ? binding.fieldSlugs : {};

          return (
            <div
              key={param.name}
              className="space-y-2"
              data-testid={`${testId}-param-${param.name}`}
            >
              <div className="flex items-center gap-2">
                <code className="rounded bg-bg-tertiary px-1 py-0.5 text-xs text-text-secondary">
                  {param.name}
                </code>
                <span className="text-xs text-text-muted">{param.type}</span>
                {param.required && (
                  <span className="text-xs text-error-500">
                    {t('common.dictionaryPicker.paramRequiredMarker')}
                  </span>
                )}
              </div>
              <Dropdown
                value={kind}
                onChange={(value) => handleKindChange(param.name, value as BindingKindOption)}
                options={kindOptions}
                data-testid={`${testId}-kind-${param.name}`}
              />
              {kind === 'record_field' && (
                <Dropdown
                  value={siblingSlug}
                  onChange={(value) => handleSiblingChange(param.name, value)}
                  options={siblingOptions}
                  data-testid={`${testId}-field-${param.name}`}
                />
              )}
              {kind === 'parent_field' && parentField && (
                <div className="space-y-2" data-testid={`${testId}-parent-${param.name}`}>
                  {parentField.keys.map((entry) => (
                    <div key={entry.key} className="space-y-1">
                      {entry.label !== null && (
                        <p className="text-xs font-medium text-text-secondary">{entry.label}</p>
                      )}
                      <Dropdown
                        value={parentSlugs[entry.key] ?? ''}
                        onChange={(slug) => handleParentFieldChange(param.name, entry.key, slug)}
                        options={[
                          {
                            value: '',
                            label: t('common.dictionaryPicker.paramBindingFieldPlaceholder'),
                          },
                          ...entry.fields.map((f) => ({ value: f.slug, label: f.name })),
                        ]}
                        disabled={entry.isLoading === true}
                        aria-label={
                          entry.label !== null
                            ? `${parentField.label}: ${entry.label}`
                            : parentField.label
                        }
                        data-testid={`${testId}-parent-field-${param.name}-${entry.key}`}
                      />
                      {entry.loadError === true && (
                        <p
                          className="text-xs text-error-500"
                          data-testid={`${testId}-parent-error-${param.name}-${entry.key}`}
                        >
                          {t('common.dictionaryPicker.parentFieldLoadError')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {kind === 'constant' && (
                <Input
                  value={constantText}
                  onChange={(e) => handleConstantChange(param.name, e.target.value)}
                  placeholder={t('common.dictionaryPicker.constantValuePlaceholder')}
                  data-testid={`${testId}-constant-${param.name}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
